import axios from "axios";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import { toast } from "react-toastify";
import ButtonLoader from "../../components/Loader/ButtonLoader";
import envValues from "../../enviornment";

const EMPTY_FORM_ERRORS = {
  items: "",
  refundType: "",
  partialRefundAmount: "",
  costAbsorber: "",
  splitRatio: "",
  cancelReason: "",
  customReason: "",
  form: "",
};

const hasFormErrors = (errors) =>
  Object.values(errors).some((message) => Boolean(message));

const IssueRefundModal = ({ show, onHide, orderDetails, token, onSuccess }) => {
  const [modifiedItems, setModifiedItems] = useState([]);
  const [costAbsorber, setCostAbsorber] = useState("Cafe");
  const [cancelReason, setCancelReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);
  const [refundType, setRefundType] = useState("");
  const [partialRefundAmount, setPartialRefundAmount] = useState("");
  const [splitRatio, setSplitRatio] = useState({ cafe: 50, baristi: 50 });
  const [showValidation, setShowValidation] = useState(false);
  const [formErrors, setFormErrors] = useState(EMPTY_FORM_ERRORS);

  useEffect(() => {
    if (!show || !orderDetails?.items) return;
    setModifiedItems(
      orderDetails.items.map((item) => ({
        ...JSON.parse(JSON.stringify(item)),
        isChecked: false,
      }))
    );
    setCostAbsorber("Cafe");
    setCancelReason("");
    setCustomReason("");
    setSelectAll(false);
    setRefundType("");
    setPartialRefundAmount("");
    setSplitRatio({ cafe: 50, baristi: 50 });
    setShowValidation(false);
    setFormErrors(EMPTY_FORM_ERRORS);
  }, [show, orderDetails]);

  const clearFormError = (field) => {
    setFormErrors((prev) => ({
      ...prev,
      [field]: "",
      form: "",
    }));
  };

  const calculateItemLineTotal = (item) => {
    const quantity = Number(item.quantity) || 0;
    const basePrice = (item.vendorUnitPrice || 0) * quantity;
    const addOnsTotal = item?.addOns?.length
      ? item.addOns.reduce(
          (total, set) =>
            total +
            set.selectedAddOns.reduce(
              (setTotal, addon) => setTotal + (addon.addOnPrice || 0),
              0
            ),
          0
        ) * quantity
      : 0;
    return basePrice + addOnsTotal;
  };

  const getVariationName = (item) => {
    if (!item?.variation) return "NA";
    return (
      item?.drink?.variations?.find(
        (option) => option?.itemId === item?.variation
      )?.name ?? "NA"
    );
  };

  const isItemAlreadyCanceled = (index) =>
    Boolean(orderDetails?.items[index]?.isCanceled);

  const hasItemModification = () =>
    modifiedItems.some((item, index) => {
      if (isItemAlreadyCanceled(index)) return false;
      const originalQty = orderDetails.items[index].quantity;
      return item.isChecked || Number(item.quantity) !== originalQty;
    });

  const calculateRefundAmount = () => {
    const selectedItemsTotal = modifiedItems.reduce((total, item, index) => {
      if (item.isChecked && !isItemAlreadyCanceled(index)) {
        return total + calculateItemLineTotal(item);
      }
      return total;
    }, 0);

    if (refundType === "full") return orderDetails?.totalAmount;
    if (refundType === "partial") return parseFloat(partialRefundAmount) || 0;
    if (refundType === "none") return 0;
    return selectedItemsTotal > 0 ? selectedItemsTotal : 0;
  };

  const validateRefundForm = () => {
    const errors = { ...EMPTY_FORM_ERRORS };

    if (!refundType) errors.refundType = "Please select a refund type.";
    if (refundType === "none" && !hasItemModification()) {
      errors.items =
        "Select at least one item to cancel or reduce its quantity.";
    }

    if (refundType === "partial") {
      const partialAmount = parseFloat(String(partialRefundAmount).trim());
      if (!String(partialRefundAmount).trim() || Number.isNaN(partialAmount)) {
        errors.partialRefundAmount = "Please enter a partial refund amount.";
      } else if (partialAmount <= 0) {
        errors.partialRefundAmount =
          "Partial refund amount must be greater than $0.00.";
      } else if (partialAmount > orderDetails?.totalAmount) {
        errors.partialRefundAmount = `Amount cannot exceed $${Number(
          orderDetails?.totalAmount
        ).toFixed(2)}.`;
      }
    }

    if (!costAbsorber) errors.costAbsorber = "Please select who absorbs the cost.";
    if (costAbsorber === "Split") {
      if (splitRatio.cafe < 0 || splitRatio.cafe > 100) {
        errors.splitRatio = "Cafe split must be between 0% and 100%.";
      } else if (splitRatio.cafe + splitRatio.baristi !== 100) {
        errors.splitRatio = "Split ratio must total 100%.";
      }
    }

    const trimmedCustomReason = customReason.trim();
    if (cancelReason === "Custom") {
      if (!trimmedCustomReason) {
        errors.customReason = "Please enter a custom cancellation reason.";
      } else if (trimmedCustomReason.length < 3) {
        errors.customReason = "Custom reason must be at least 3 characters.";
      }
    } else if (!cancelReason) {
      if (!trimmedCustomReason) {
        errors.cancelReason = "Please select or enter a cancellation reason.";
      } else if (trimmedCustomReason.length < 3) {
        errors.customReason = "Reason must be at least 3 characters.";
      }
    }

    return errors;
  };

  const handleSelectAllItems = (isChecked) => {
    clearFormError("items");
    setSelectAll(isChecked);
    setModifiedItems(
      modifiedItems.map((item, i) => {
        if (isItemAlreadyCanceled(i)) return item;
        return { ...item, isChecked };
      })
    );
  };

  const handleItemCheckChange = (index, isChecked) => {
    clearFormError("items");
    const updated = [...modifiedItems];
    updated[index].isChecked = isChecked;
    updated[index].isCanceled = !isChecked;
    setModifiedItems(updated);
    const allChecked = updated.every((itm, i) =>
      isItemAlreadyCanceled(i) ? true : itm.isChecked
    );
    setSelectAll(allChecked);
  };

  const handleItemQuantityChange = (index, input) => {
    clearFormError("items");
    if (input === "") {
      const updated = [...modifiedItems];
      updated[index].quantity = "";
      setModifiedItems(updated);
      return;
    }
    if (!/^\d+$/.test(input)) return;
    const newQty = parseInt(input, 10);
    const maxQty = orderDetails.items[index].quantity;
    if (newQty >= 0 && newQty <= maxQty) {
      const updated = [...modifiedItems];
      updated[index].quantity = newQty;
      setModifiedItems(updated);
    }
  };

  const handleItemQuantityBlur = (index, input) => {
    const maxQty = orderDetails.items[index].quantity;
    const updated = [...modifiedItems];
    if (input === "" || Number.isNaN(parseInt(input, 10))) {
      updated[index].quantity = 0;
      setModifiedItems(updated);
      clearFormError("items");
      return;
    }
    if (parseInt(input, 10) > maxQty) {
      updated[index].quantity = maxQty;
      setModifiedItems(updated);
    }
    clearFormError("items");
  };

  const handleConfirmRefund = async () => {
    const errors = validateRefundForm();
    setFormErrors(errors);
    setShowValidation(true);
    if (hasFormErrors(errors)) return;

    setIsSubmittingRefund(true);
    try {
      const payload = {
        orderId: orderDetails._id,
        updatedItems:
          refundType === "none"
            ? modifiedItems.map((item) => ({
                drinkId: item.drink?._id,
                quantity: item.isChecked ? 0 : item.quantity,
                variationId: item.variation,
              }))
            : null,
        costAbsorber,
        cancelReason: customReason ? customReason : cancelReason,
        refundType,
        refundAmount: calculateRefundAmount(),
        splitRatio: costAbsorber === "Split" ? splitRatio : null,
      };

      const response = await axios.put(
        `${envValues.REACT_APP_API_ENDPOINT}/orders/modify-items`,
        payload,
        { headers: { Authorization: token } }
      );

      if (response.status !== 200) {
        throw new Error("Failed to update order.");
      }

      toast.success("Refund issued successfully.");
      onHide();
      onSuccess?.();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update order. Please try again.";
      setFormErrors((prev) => ({ ...prev, form: message }));
    } finally {
      setIsSubmittingRefund(false);
    }
  };

  if (!orderDetails) return null;

  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Items to Cancel</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {formErrors.form && (
          <Alert variant="danger" className="mb-3">
            {formErrors.form}
          </Alert>
        )}
        {showValidation && hasFormErrors(formErrors) && !formErrors.form && (
          <Alert variant="danger" className="mb-3">
            Please fix the highlighted fields before confirming.
          </Alert>
        )}
        {modifiedItems?.length > 1 && (
          <Form.Check
            type="checkbox"
            id="select-all-refund"
            label="Select All"
            checked={selectAll}
            onChange={(e) => handleSelectAllItems(e.target.checked)}
            className="mb-3"
          />
        )}
        {showValidation && formErrors.items && (
          <div className="text-danger small mb-2">{formErrors.items}</div>
        )}
        {modifiedItems?.map((item, index) => {
          const maxQty = orderDetails.items[index].quantity;
          const isAlreadyCanceled = isItemAlreadyCanceled(index);
          return (
            <div
              key={index}
              className="mb-3 p-2 border rounded"
              style={{
                opacity: isAlreadyCanceled ? 0.6 : 1,
                pointerEvents: isAlreadyCanceled ? "none" : "auto",
                backgroundColor: isAlreadyCanceled ? "#f5f5f5" : "white",
              }}
            >
              <Form.Check
                type="checkbox"
                className="mb-2"
                id={`cancel-item-${index}`}
                checked={item?.isChecked}
                label={
                  <div>
                    <div style={{ fontWeight: 600 }}>{item?.drink?.name}</div>
                    <div style={{ fontSize: "0.9em", color: "#666" }}>
                      {getVariationName(item)}
                    </div>
                  </div>
                }
                onChange={(e) =>
                  handleItemCheckChange(index, e.target.checked)
                }
              />
              {item?.addOns?.length > 0 && (
                <div
                  style={{
                    fontSize: "0.85em",
                    color: "#555",
                    marginLeft: "25px",
                    marginBottom: "8px",
                  }}
                >
                  <strong>Add-Ons:</strong>
                  <div style={{ marginTop: "4px" }}>
                    {item.addOns.flatMap((set) =>
                      set.selectedAddOns.map((addon, addonIdx) => (
                        <div
                          key={`${index}-addon-${addonIdx}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "2px 0",
                          }}
                        >
                          <span>• {addon.addOnName}</span>
                          <span>
                            {addon.addOnPrice
                              ? `$${(addon.addOnPrice || 0).toFixed(2)}`
                              : ""}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center">
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(item.quantity)}
                  onChange={(e) =>
                    handleItemQuantityChange(index, e.target.value)
                  }
                  onBlur={(e) =>
                    handleItemQuantityBlur(index, e.target.value)
                  }
                  style={{ width: "70px", marginRight: "10px" }}
                />
                <div style={{ fontSize: "0.9em", color: "#888" }}>
                  of {maxQty}
                </div>
                <div style={{ fontWeight: "bold", marginLeft: "auto" }}>
                  ${calculateItemLineTotal(item).toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
        <Form.Group className="mt-4" controlId="refund-type">
          <Form.Label>
            <strong>Refund Type</strong>{" "}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={refundType}
            onChange={(e) => {
              setRefundType(e.target.value);
              if (e.target.value !== "partial") setPartialRefundAmount("");
              clearFormError("refundType");
              clearFormError("partialRefundAmount");
              clearFormError("items");
            }}
            isInvalid={showValidation && Boolean(formErrors.refundType)}
          >
            <option value="">Select refund type</option>
            <option value="full">Issue Full Refund</option>
            <option value="partial">Issue Partial Refund</option>
            <option value="none">No Refund</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {formErrors.refundType}
          </Form.Control.Feedback>
          {["card", "Apple Pay", "Google Pay"].includes(
            orderDetails?.paymentMethod
          ) && (
            <Alert variant="info" className="mt-3 mb-0">
              This order was paid by {orderDetails.paymentMethod}. The refund
              will be sent back to that card and can take several business days
              to appear.
            </Alert>
          )}
        </Form.Group>
        {refundType === "partial" && (
          <Form.Group className="mt-3" controlId="partial-refund-amount">
            <Form.Label>
              <strong>Partial Refund Amount</strong>{" "}
              <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup hasValidation>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                max={orderDetails?.totalAmount}
                placeholder="Enter refund amount"
                value={partialRefundAmount}
                isInvalid={
                  showValidation && Boolean(formErrors.partialRefundAmount)
                }
                onChange={(e) => {
                  setPartialRefundAmount(e.target.value);
                  clearFormError("partialRefundAmount");
                }}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.partialRefundAmount}
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              Maximum refundable amount: $
              {Number(orderDetails?.totalAmount || 0).toFixed(2)}
            </Form.Text>
          </Form.Group>
        )}
        <Form.Group className="mt-4" controlId="cost-absorber">
          <Form.Label>
            <strong>Who should absorb the cost difference?</strong>{" "}
            <span className="text-danger">*</span>
          </Form.Label>
          {["Baristi", "Cafe", "Split"].map((value) => (
            <Form.Check
              key={value}
              type="radio"
              label={value === "Split" ? "Split Cafe/Baristi" : value}
              name="costAbsorber"
              id={`cost-absorber-${value}`}
              value={value}
              checked={costAbsorber === value}
              onChange={(e) => {
                setCostAbsorber(e.target.value);
                clearFormError("costAbsorber");
                clearFormError("splitRatio");
              }}
              className="mb-2"
              isInvalid={showValidation && Boolean(formErrors.costAbsorber)}
            />
          ))}
          {showValidation && formErrors.costAbsorber && (
            <div className="text-danger small">{formErrors.costAbsorber}</div>
          )}
          {costAbsorber === "Split" && (
            <div className="mt-2 ms-4">
              <Form.Label>
                <strong>Split Ratio</strong>{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0">Cafe:</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={splitRatio.cafe}
                    onChange={(e) => {
                      const cafe = parseInt(e.target.value, 10) || 0;
                      setSplitRatio({ cafe, baristi: 100 - cafe });
                      clearFormError("splitRatio");
                    }}
                    isInvalid={
                      showValidation && Boolean(formErrors.splitRatio)
                    }
                    style={{ width: "80px" }}
                  />
                  <span>%</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0">Baristi:</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={splitRatio.baristi}
                    readOnly
                    style={{ width: "80px", backgroundColor: "#f8f9fa" }}
                  />
                  <span>%</span>
                </div>
              </div>
              <Form.Text className="text-muted">
                Total: {splitRatio.cafe + splitRatio.baristi}%
              </Form.Text>
              {showValidation && formErrors.splitRatio && (
                <div className="text-danger small mt-1">
                  {formErrors.splitRatio}
                </div>
              )}
            </div>
          )}
        </Form.Group>
        <Form.Group className="mt-3" controlId="cancel-reason">
          <Form.Label>
            <strong>Reason for the cancellation</strong>{" "}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              if (e.target.value !== "Custom") setCustomReason("");
              clearFormError("cancelReason");
              clearFormError("customReason");
            }}
            isInvalid={showValidation && Boolean(formErrors.cancelReason)}
          >
            <option value="">Select a reason</option>
            <option value="Item(s) unavailable">Item(s) unavailable</option>
            <option value="Customer canceled">Customer canceled</option>
            <option value="Cafe out of stock">Cafe out of stock</option>
            <option value="Customer Satisfaction">Customer Satisfaction</option>
            <option value="Custom">Custom reason</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {formErrors.cancelReason}
          </Form.Control.Feedback>
          {(cancelReason === "Custom" || cancelReason === "") && (
            <>
              <Form.Control
                type="text"
                placeholder="Enter reason for cancellation"
                value={customReason}
                onChange={(e) => {
                  setCustomReason(e.target.value);
                  clearFormError("customReason");
                  clearFormError("cancelReason");
                }}
                isInvalid={
                  showValidation && Boolean(formErrors.customReason)
                }
                className="mt-2"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.customReason}
              </Form.Control.Feedback>
            </>
          )}
        </Form.Group>
      </Modal.Body>
      <div
        style={{
          padding: "15px",
          borderTop: "1px solid #dee2e6",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <strong>Refund Amount:</strong>
          <span
            style={{
              fontSize: "1.2em",
              fontWeight: "bold",
              color: "#198754",
            }}
          >
            ${calculateRefundAmount().toFixed(2)}
          </span>
        </div>
      </div>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmittingRefund}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmRefund}
          disabled={isSubmittingRefund}
        >
          {isSubmittingRefund ? (
            <>
              <ButtonLoader /> Confirming...
            </>
          ) : (
            "Confirm"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

IssueRefundModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  orderDetails: PropTypes.object,
  token: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
};

export default IssueRefundModal;
